import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Chip, Button } from "@heroui/react";
import {
  CaretDown,
  CaretRight,
  ArrowsLeftRight,
  ArrowRight,
  ArrowsOut,
  Info,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { EntityInstance, EntityRelation } from "../types";
import PropertyCard from "./PropertyCard";

interface RelationCardProps {
  relation: EntityRelation;
  expanded: boolean;
  onToggleExpand: () => void;
  depth: number;
  searchQuery?: string;
  registerMatchRef?: (ref: HTMLDivElement | null, relationName?: string) => void;
}

const RelationCard: React.FC<RelationCardProps> = ({
  relation,
  expanded,
  onToggleExpand,
  depth,
  searchQuery,
  registerMatchRef,
}) => {
  const { session } = useSession();
  const [targetTableSchema, setTargetTableSchema] = useState<any>(null);
  const [loadingSchema, setLoadingSchema] = useState<boolean>(false);

  // Fetch target table schema when relation is expanded (always, not just when no data)
  useEffect(() => {
    // Always fetch schema when expanded to show available fields
    if (expanded && !targetTableSchema && !loadingSchema && session) {
      fetchTargetTableSchema();
    }
  }, [expanded, session]);

  const fetchTargetTableSchema = async () => {
    setLoadingSchema(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/schema`,
        { method: "GET" },
        session,
      );

      console.log("Wat er gezocht word " + relation.targetTable);

      if (response.ok) {
        const schemaData = await response.json();
        console.log("DIT IS WAT DE RESPONSE GEEFT " + schemaData);
        const table = schemaData.detailedTables.find(
          (t: any) => t.tableName === relation.targetTable,
        );
        setTargetTableSchema(table);
      }
    } catch (error) {
      console.error("Error fetching table schema:", error);
    } finally {
      setLoadingSchema(false);
    }
  };

  const getRelationIcon = (type: string) => {
    switch (type) {
      case "ManyToOne":
        return <ArrowRight size={16} weight="bold" />;
      case "OneToMany":
        return <ArrowsOut size={16} weight="bold" />;
      case "ManyToMany":
        return <ArrowsLeftRight size={16} weight="bold" />;
      case "OneToOne":
        return <ArrowRight size={16} weight="duotone" />;
      default:
        return <ArrowRight size={16} />;
    }
  };

  const getRelationColor = (
    type: string,
  ): "default" | "primary" | "secondary" | "success" | "warning" => {
    switch (type) {
      case "ManyToOne":
        return "primary";
      case "OneToMany":
        return "secondary";
      case "ManyToMany":
        return "warning";
      case "OneToOne":
        return "success";
      default:
        return "default";
    }
  };

  const renderEntityData = (entity: EntityInstance) => {
    // Show ALL properties - don't filter out any fields
    const properties = Object.entries(entity.properties || {});

    return (
      <div className="mt-4 space-y-2">
        {/* Entity Properties */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {properties.map(([key, value]) => {
            // Find type from schema if available
            const columnType = targetTableSchema?.columns?.find(
              (col: any) => col.columnName === key,
            )?.dataType;

            const isMatch = searchQuery ? key.toLowerCase().includes(searchQuery.toLowerCase()) : false;

            return (
              <PropertyCard
                key={key}
                name={key}
                value={value}
                tableName={entity.tableName}
                type={columnType}
                highlighted={isMatch}
                registerMatchRef={isMatch ? registerMatchRef : undefined}
              />
            );
          })}
        </div>

        {/* Nested Relations */}
        {entity.relations && entity.relations.length > 0 && (
          <div className="mt-4 ml-4 space-y-2 border-l-2 border-purple-200 pl-4">
            <h4 className="mb-2 text-xs font-semibold text-purple-700 uppercase">
              Relaties van {entity.tableName}
            </h4>
            {entity.relations.map((nestedRelation, idx) => (
              <RelationCard
                key={`${nestedRelation.relationName}-${idx}`}
                relation={nestedRelation}
                expanded={false}
                onToggleExpand={() => {}}
                depth={depth + 1}
                searchQuery={searchQuery}
                registerMatchRef={registerMatchRef}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSchemaSection = () => {
    if (!targetTableSchema) return null;

    return (
      <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
        <div className="mb-3 flex items-start gap-2">
          <Info size={18} className="mt-0.5 flex-shrink-0 text-indigo-600" />
          <div>
            <h4 className="text-sm font-semibold text-indigo-900">
              Beschikbare Velden ({targetTableSchema.columns?.length || 0})
            </h4>
            <p className="mt-1 text-xs text-indigo-700">
              Deze velden zijn beschikbaar in {relation.targetTable}
            </p>
          </div>
        </div>

        {/* Show field names as chips */}
        <div className="flex flex-wrap gap-2">
          {targetTableSchema.columns?.map((col: any) => (
            <Chip
              key={col.columnName}
              size="sm"
              variant="flat"
              color="secondary"
              className="text-xs"
            >
              {col.columnName}
              {col.dataType && (
                <span className="ml-1 opacity-70">: {col.dataType}</span>
              )}
            </Chip>
          ))}
        </div>
      </div>
    );
  };

  const renderRelationData = () => {
    // Always show schema section first if available
    const schemaSection = renderSchemaSection();

    // If no data, we need to show schema structure
    if (!relation.data) {
      // If still loading schema, show loading state
      if (loadingSchema) {
        return (
          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
            Schema laden...
          </div>
        );
      }

      // If we have schema, show it with NULL values
      if (targetTableSchema) {
        const mockProperties: Record<string, any> = {};

        // Create mock properties with NULL values from schema columns
        targetTableSchema.columns.forEach((col: any) => {
          mockProperties[col.columnName] = null;
        });

        const mockEntity: EntityInstance = {
          tableName: relation.targetTable,
          primaryKey: null,
          properties: mockProperties,
          relations: [],
        };

        return (
          <div className="mt-4 space-y-4">
            {schemaSection}

            {/* Info message */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <Info size={20} className="mt-0.5 flex-shrink-0 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Deze entiteit heeft geen relatie met {relation.relationName}
                </p>
                <p className="mt-1 text-xs text-blue-700">
                  Hieronder zie je hoe de velden eruit zien met NULL waarden.
                </p>
              </div>
            </div>

            {/* Schema structure with NULL values */}
            {renderEntityData(mockEntity)}
          </div>
        );
      }

      // Schema not available and not loading - shouldn't happen but show fallback
      return (
        <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
          <p className="text-sm font-medium text-yellow-900">
            Schema kon niet worden geladen
          </p>
          <p className="mt-1 text-xs text-yellow-700">
            Probeer de relatie opnieuw te openen
          </p>
        </div>
      );
    }

    // Loading schema state for when we DO have data
    if (loadingSchema) {
      return (
        <div className="mt-4 rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
          Schema laden...
        </div>
      );
    }

    if (Array.isArray(relation.data)) {
      if (relation.data.length === 0) {
        // Show schema for empty arrays too
        if (targetTableSchema) {
          const mockProperties: Record<string, any> = {};
          targetTableSchema.columns.forEach((col: any) => {
            mockProperties[col.columnName] = null;
          });

          const mockEntity: EntityInstance = {
            tableName: relation.targetTable,
            primaryKey: null,
            properties: mockProperties,
            relations: [],
          };

          return (
            <div className="mt-4 space-y-4">
              {schemaSection}

              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <Info
                  size={20}
                  className="mt-0.5 flex-shrink-0 text-blue-600"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    Geen {relation.relationName} gevonden voor deze entiteit
                  </p>
                  <p className="mt-1 text-xs text-blue-700">
                    Hieronder zie je hoe de velden eruit zien met NULL waarden.
                  </p>
                </div>
              </div>
              {renderEntityData(mockEntity)}
            </div>
          );
        }

        return (
          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500 italic">
            Geen gerelateerde items gevonden
          </div>
        );
      }

      return (
        <div className="mt-4 space-y-4">
          {schemaSection}

          {/* Success message */}
          <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
            <Info size={20} className="mt-0.5 flex-shrink-0 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">
                {relation.data.length} {relation.relationName} gevonden
              </p>
              <p className="mt-1 text-xs text-green-700">
                Hieronder zie je de gevonden entiteiten met hun waarden.
              </p>
            </div>
          </div>

          {relation.data.map((entity, idx) => (
            <div
              key={`${entity.primaryKey}-${idx}`}
              className="rounded-lg border border-gray-200 bg-gray-50/50 p-4"
            >
              <div className="mb-2 text-xs font-semibold text-gray-600">
                Item {idx + 1} van{" "}
                {relation.data && Array.isArray(relation.data)
                  ? relation.data.length
                  : 0}
              </div>
              {renderEntityData(entity)}
            </div>
          ))}
        </div>
      );
    }

    // Single entity (ManyToOne, OneToOne)
    return (
      <div className="mt-4 space-y-4">
        {schemaSection}
        {renderEntityData(relation.data)}
      </div>
    );
  };

  const indentStyle = {
    marginLeft: `${depth * 1}rem`,
  };

  return (
    <Card
      className="border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50"
      style={indentStyle}
    >
      <CardHeader className="pb-2">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={onToggleExpand}
              className="min-w-unit-6 h-6 w-6"
            >
              {expanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
            </Button>

            <span className="font-semibold text-gray-800">
              {relation.relationName}
            </span>

            <Chip
              size="sm"
              variant="flat"
              color={getRelationColor(relation.relationType)}
              startContent={getRelationIcon(relation.relationType)}
            >
              {relation.relationType}
            </Chip>

            <Chip size="sm" variant="flat" color="default">
              {relation.targetTable}
            </Chip>

            {relation.isNullable && (
              <Chip size="sm" variant="flat" color="warning">
                Nullable
              </Chip>
            )}
          </div>

          {Array.isArray(relation.data) && relation.data.length > 0 && (
            <Chip size="sm" variant="solid" color="secondary">
              {relation.data.length} items
            </Chip>
          )}
        </div>
      </CardHeader>

      {expanded && <CardBody className="pt-2">{renderRelationData()}</CardBody>}
    </Card>
  );
};

export default RelationCard;
